import React, { useState, useEffect, useCallback, useMemo } from 'react';
import Select from 'react-select';
import { useDropzone } from 'react-dropzone';
import {
  Card,
  CardBody,
  FormGroup,
  Label,
  Input,
  Button,
  Row,
  Col,
  Badge,
  Spinner,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Alert,
} from 'reactstrap';
import { toast } from 'react-toastify';
import Swal from 'sweetalert2';
import FeatherIcon from 'feather-icons-react';
import jwt_decode from 'jwt-decode';
import axios from '../api/axios';
import { getAllAvailabilities } from '../SetAvailability/Helpers/Api';

const DAYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];

const formatMinutes = (minutes) => {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
};

const getDayNameFromDate = (dateStr) => {
  const [year, month, day] = dateStr.split('-').map(Number);
  const date = new Date(year, month - 1, day);
  const dayIndex = date.getDay();
  return DAYS[dayIndex === 0 ? 6 : dayIndex - 1];
};

// Local calendar date as YYYY-MM-DD. Used for "today" so it matches the
// local clock — `toISOString()` is UTC and rolls over a day early in the
// evening for timezones behind UTC, which wrongly hides a day's slots.
const localISODate = (d = new Date()) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

const timeToMinutes = (timeStr) => {
  if (!timeStr) return 0;
  const [h, m] = timeStr.split(':').map(Number);
  return h * 60 + m;
};

const signedUrlCache = new Map();
const pendingSignedRequests = new Map();

const getSignedUrlsBatched = async (urls) => {
  if (!urls?.length) return {};
  const result = {};
  const toFetch = [];
  for (const url of urls) {
    if (signedUrlCache.has(url)) {
      result[url] = signedUrlCache.get(url);
      continue;
    }
    if (pendingSignedRequests.has(url)) {
      result[url] = await pendingSignedRequests.get(url);
      continue;
    }
    toFetch.push(url);
  }

  if (toFetch.length === 0) return result;
  const promise = axios
    .post('v2/inventory/consume/get-signed-urls', { urls: toFetch })
    .then((res) => {
      if (res.data.success) {
        res.data.signed_urls.forEach(({ original_url, signed_url }) => {
          if (signed_url) signedUrlCache.set(original_url, signed_url);
        });
      }
      return res;
    })
    .finally(() => {
      toFetch.forEach((u) => pendingSignedRequests.delete(u));
    });

  toFetch.forEach((u) => pendingSignedRequests.set(u, promise.then(() => signedUrlCache.get(u) || u)));

  await promise;

  toFetch.forEach((url) => {
    result[url] = signedUrlCache.get(url) || url;
  });

  return result;
};

const generateTimeOptions = (() => {
  const slots = [];
  for (let i = 1; i <= 12; i++) {
    const total = i * 10;
    const h = Math.floor(total / 60);
    const m = total % 60;
    let label = '';
    if (h) label += `${h}h`;
    if (m) label += ` ${m}m`;
    slots.push({ label: label.trim() || '10m', value: total });
  }
  return slots;
})();

const customSelectStyles = {
  control: (provided) => ({
    ...provided,
    minHeight: '38px',
    borderColor: '#ced4da',
    boxShadow: 'none',
    '&:hover': { borderColor: '#80bdff' },
  }),
  option: (provided) => ({
    ...provided,
    padding: '8px 12px',
  }),
};

const SchedulingSection = React.memo(
  ({
    scheduleDate,
    setScheduleDate,
    selectedSlot,
    setSelectedSlot,
    technicianName,
    setTechnicianName,
    technicianPhone,
    setTechnicianPhone,
    loadingAvailability,
    availableSlots,
    loading,
  }) => (
    <Card className="bg-info bg-opacity-10 border-0 mt-3">
      <CardBody className="p-4">
        <Row className="g-4">
          <Col md={6}>
            <FormGroup>
              <Label className="fw-semibold">
                Date <span className="text-danger">*</span>
              </Label>
              <Input
                type="date"
                value={scheduleDate}
                onChange={(e) => {
                  setScheduleDate(e.target.value);
                  setSelectedSlot(null);
                }}
                min={localISODate()}
              />
            </FormGroup>
          </Col>
          <Col md={6}>
            <FormGroup>
              <Label className="fw-semibold">
                Time Slot <span className="text-danger">*</span>
              </Label>
              {loadingAvailability ? (
                <div className="d-flex align-items-center p-3 bg-light rounded">
                  <Spinner size="sm" className="me-2" /> Loading available slots...
                </div>
              ) : availableSlots.length === 0 ? (
                <Alert color="warning" className="mb-0 py-2 small">
                  <FeatherIcon icon="info" className="me-2" />
                  No available slots on selected date
                </Alert>
              ) : (
                <Select
                  value={selectedSlot}
                  onChange={setSelectedSlot}
                  options={availableSlots}
                  styles={customSelectStyles}
                  placeholder="Select time slot..."
                  isClearable
                  isDisabled={loading || loadingAvailability}
                />
              )}
            </FormGroup>
          </Col>
          <Col md={6}>
            <FormGroup>
              <Label className="fw-semibold">
                Technician Name <span className="text-danger">*</span>
              </Label>
              <Input
                type="text"
                value={technicianName}
                onChange={(e) => setTechnicianName(e.target.value)}
                placeholder="Enter technician name"
              />
            </FormGroup>
          </Col>
          <Col md={6}>
            <FormGroup>
              <Label className="fw-semibold">
                Technician Phone <span className="text-danger">*</span>
              </Label>
              <Input
                type="tel"
                value={technicianPhone}
                onChange={(e) => setTechnicianPhone(e.target.value)}
                placeholder="Technician phone"
              />
            </FormGroup>
          </Col>
        </Row>
      </CardBody>
    </Card>
  )
);

const ProgressSection = React.memo(
  ({
    progressNotes,
    setProgressNotes,
    partsOrdered,
    setPartsOrdered,
    expectedArrivalDate,
    setExpectedArrivalDate,
    followUpDate,
    setFollowUpDate,
    loading,
  }) => (
    <div className="mb-4">
      <Card className="bg-warning bg-opacity-10 border-0">
        <CardBody className="p-4">
          <FormGroup className="mb-4">
            <Label className="fw-semibold">
              Progress Notes <span className="text-danger">*</span>
            </Label>
            <Input
              type="textarea"
              rows={3}
              value={progressNotes}
              onChange={(e) => setProgressNotes(e.target.value)}
              placeholder="Describe the work completed, issues identified, and next steps. Please be as detailed as possible"
            />
          </FormGroup>
          <Row className="g-4">
            <Col md={4}>
              <FormGroup>
                <Label className="fw-semibold">Parts Ordered</Label>
                <Input
                  value={partsOrdered}
                  onChange={(e) => setPartsOrdered(e.target.value)}
                  placeholder="List parts ordered (if any)"
                  disabled={loading}
                />
              </FormGroup>
            </Col>
            <Col md={4}>
              <FormGroup>
                <Label className="fw-semibold">Expected Arrival</Label>
                <Input
                  type="date"
                  value={expectedArrivalDate}
                  onChange={(e) => setExpectedArrivalDate(e.target.value)}
                  disabled={loading}
                />
              </FormGroup>
            </Col>
            <Col md={4}>
              <FormGroup>
                <Label className="fw-semibold">Follow-up Date</Label>
                <Input
                  type="date"
                  value={followUpDate}
                  onChange={(e) => setFollowUpDate(e.target.value)}
                  disabled={loading}
                />
              </FormGroup>
            </Col>
          </Row>
        </CardBody>
      </Card>
    </div>
  )
);

const MediaWithSignedUrl = React.memo(
  ({
    mediaUrl,
    alt = '',
    className = '',
    style = {},
    onClick,
    isThumbnail = false,
    mediaType = null,
  }) => {
    const [signedUrl, setSignedUrl] = useState(null);
    const [loadingState, setLoadingState] = useState(true);

    const isVideo = mediaType?.startsWith('video/') || /\.(mp4|webm|ogg)$/i.test(mediaUrl);

    useEffect(() => {
      let mounted = true;

      const load = async () => {
        if (
          mediaUrl.startsWith('blob:') ||
          mediaUrl.startsWith('data:') ||
          mediaUrl.includes('?') ||
          mediaUrl.includes('Expires=') ||
          mediaUrl.includes('Signature=') ||
          mediaUrl.includes('X-Amz-')
        ) {
          if (mounted) {
            setSignedUrl(mediaUrl);
            setLoadingState(false);
          }
          return;
        }

        try {
          const batch = await getSignedUrlsBatched([mediaUrl]);
          if (mounted) setSignedUrl(batch[mediaUrl] || mediaUrl);
        } catch {
          if (mounted) setSignedUrl(mediaUrl);
        } finally {
          if (mounted) setLoadingState(false);
        }
      };

      load();

      return () => {
        mounted = false;
      };
    }, [mediaUrl]);

    if (loadingState) {
      return (
        <div className={className} style={style}>
          <Spinner size="sm" />
        </div>
      );
    }

    const imgStyle = isThumbnail ? { width: '64px', height: '64px', objectFit: 'cover' } : {};
    const videoStyle = isThumbnail ? { width: '64px', height: '64px', objectFit: 'cover' } : {};

    if (isVideo) {
      return (
        <div
          className="position-relative"
          style={{ cursor: isThumbnail ? 'pointer' : 'default' }}
          onClick={isThumbnail ? onClick : undefined}
        >
          <video
            src={signedUrl}
            controls={!isThumbnail}
            preload="metadata"
            className={className}
            style={{ ...style, ...videoStyle }}
            loading="lazy"
          />
          {isThumbnail && (
            <div
              className="position-absolute top-50 start-50 translate-middle bg-dark bg-opacity-50 rounded-circle d-flex align-items-center justify-content-center"
              style={{ width: '32px', height: '32px' }}
            >
              <FeatherIcon icon="play" size={14} className="text-white ms-1" />
            </div>
          )}
        </div>
      );
    }

    return (
      <img
        src={signedUrl}
        alt={alt}
        className={className}
        style={{ ...style, ...imgStyle }}
        onClick={onClick}
        loading="lazy"
        decoding="async"
        width={isThumbnail ? 64 : undefined}
        height={isThumbnail ? 64 : undefined}
      />
    );
  }
);

const MaintenanceFlowDetailsForm = ({
  item,
  isOpen,
  onClose,
  getTechnicalStaffTickets,
  getTotalTickets,
  targetStatus,
}) => {
  const ticketStatus = targetStatus || item?.status || 'Open';

  const [currentUser, setCurrentUser] = useState(null);
  const [scheduleDate, setScheduleDate] = useState('');
  const [availableSlots, setAvailableSlots] = useState([]);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [technicianName, setTechnicianName] = useState('');
  const [technicianPhone, setTechnicianPhone] = useState('');
  const [loadingAvailability, setLoadingAvailability] = useState(false);
  const [progressNotes, setProgressNotes] = useState('');
  const [partsOrdered, setPartsOrdered] = useState('');
  const [expectedArrivalDate, setExpectedArrivalDate] = useState('');
  const [followUpDate, setFollowUpDate] = useState('');
  const [closureNotes, setClosureNotes] = useState('');
  const [attachments, setAttachments] = useState([]);
  const [pendingAttachments, setPendingAttachments] = useState([]);
  const [timeTaken, setTimeTaken] = useState(item?.timeTaken || 0);
  const [availableItems, setAvailableItems] = useState([]);
  const [selectedItems, setSelectedItems] = useState([]);
  const [notes, setNotes] = useState('');
  const [imagePreviewModal, setImagePreviewModal] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [isSchedulingOpen, setIsSchedulingOpen] = useState(false);
  const [workflow, setWorkflow] = useState(null);
  const [ticketHistory, setTicketHistory] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [uploadingAttachments, setUploadingAttachments] = useState(false);

  const getCurrentUser = useCallback(() => {
    try {
      const token = localStorage.getItem('accessToken');
      if (!token) return null;
      const decoded = jwt_decode(token);
      return {
        id: decoded.id || decoded._id || decoded.sub || decoded.userId,
        role: decoded.role,
      };
    } catch {
      return null;
    }
  }, []);

  useEffect(() => {
    const user = getCurrentUser();
    if (user) setCurrentUser(user);
  }, [getCurrentUser]);

  const loadSelfAvailability = useCallback(async () => {
    if (!currentUser?.id || !scheduleDate) {
      setAvailableSlots([]);
      return;
    }
    setLoadingAvailability(true);
    try {
      const { availabilities } = await getAllAvailabilities({
        person_id: currentUser.id,
        role: currentUser.role,
      });
      if (!availabilities?.length) {
        setAvailableSlots([]);
        return;
      }
      const record = availabilities[0];
      setTechnicianName(record.userName || '');
      setTechnicianPhone(record.userPhone || '');
      const dayName = getDayNameFromDate(scheduleDate).toLowerCase();
      const dayAvail = record.availability?.find((d) => d.day === dayName);
      if (dayAvail?.slots?.length) {
        const now = new Date();
        const today = localISODate(now);
        const nowMinutes = now.getHours() * 60 + now.getMinutes();
        let slots = dayAvail.slots.map((slot) => ({
          value: slot,
          label: `${formatMinutes(slot.start_time)} - ${formatMinutes(slot.end_time)}`,
          start_time: slot.start_time,
          end_time: slot.end_time,
        }));
        if (scheduleDate === today) {
          slots = slots.filter((s) => s.end_time > nowMinutes);
        }
        setAvailableSlots(slots);
      } else {
        setAvailableSlots([]);
      }
    } catch {
      setAvailableSlots([]);
    } finally {
      setLoadingAvailability(false);
    }
  }, [currentUser?.id, currentUser?.role, scheduleDate]);

  // Every time the modal opens, start the scheduler on today's local date with
  // no slot preselected — never a stale/previous date carried over.
  useEffect(() => {
    if (isOpen) {
      setScheduleDate(localISODate());
      setSelectedSlot(null);
    }
  }, [isOpen]);

  useEffect(() => {
    if (!item?._id) return;

    // Reset all per-ticket workflow fields so switching to (or reopening on) a
    // different ticket never shows the previously viewed ticket's data.
    // availableSlots is NOT cleared here — it is scoped to the technician+date,
    // not the ticket; clearing it on switch left the second ticket with no
    // slots when the date was unchanged (loader only refetches on date change).
    setScheduleDate(localISODate());
    setSelectedSlot(null);
    setTechnicianName('');
    setTechnicianPhone('');
    setProgressNotes('');
    setPartsOrdered('');
    setExpectedArrivalDate('');
    setFollowUpDate('');
    setClosureNotes('');
    setAttachments([]);
    setPendingAttachments([]);
    setWorkflow(null);
    setTicketHistory([]);

    const fetchWorkflow = async () => {
      try {
        const res = await axios.get(`v2/maintenance/workflow/${item._id}/history`);
        if (res.data.success) {
          const data = res.data.data;
          setWorkflow(data.workflow);
          setTicketHistory(data.ticketHistory || []);

          if (data.workflow?.scheduling) {
            // Date intentionally left at today — the scheduler always opens on
            // today's date rather than a previously saved one.
            setTechnicianName(data.workflow.scheduling.technicianName || '');
            setTechnicianPhone(data.workflow.scheduling.technicianPhone || '');
          }

          if (data.workflow?.progress) {
            setProgressNotes(data.workflow.progress.notes || '');
            setPartsOrdered(data.workflow.progress.partsOrdered || '');
            setExpectedArrivalDate(
              data.workflow.progress.expectedArrivalDate
                ? new Date(data.workflow.progress.expectedArrivalDate).toISOString().split('T')[0]
                : ''
            );
            setFollowUpDate(
              data.workflow.progress.followUpDate
                ? new Date(data.workflow.progress.followUpDate).toISOString().split('T')[0]
                : ''
            );
          }

          if (data.workflow?.closure) {
            setClosureNotes(data.workflow.closure.notes || '');
            setAttachments(data.workflow.closure.attachments?.map((a) => a.url) || []);
          }
        }
      } catch (err) {
        console.error(err);
      }
    };

    fetchWorkflow();
  }, [item?._id]);

  useEffect(() => {
    if (ticketStatus === 'Scheduled') {
      setIsSchedulingOpen(true);
    } else if (ticketStatus === 'Completed' && workflow?.scheduling) {
      setIsSchedulingOpen(true);
    } else {
      setIsSchedulingOpen(false);
    }
  }, [ticketStatus, workflow?.scheduling]);

  useEffect(() => {
    if (currentUser?.id && (ticketStatus === 'Scheduled' || (ticketStatus === 'Completed' && isSchedulingOpen))) {
      loadSelfAvailability();
    }
    // item?._id is a dependency so switching to another ticket on the same date
    // (which leaves scheduleDate unchanged) still reloads the technician's slots
    // and name/phone, instead of leaving them in the just-reset blank state.
  }, [currentUser?.id, item?._id, scheduleDate, ticketStatus, isSchedulingOpen, loadSelfAvailability]);

  useEffect(() => {
    if (!workflow?.scheduling?.timeSlot || !availableSlots.length) return;
    const start = timeToMinutes(workflow.scheduling.timeSlot.start);
    const end = timeToMinutes(workflow.scheduling.timeSlot.end);
    const match = availableSlots.find((s) => s.start_time === start && s.end_time === end);
    if (match) setSelectedSlot(match);
  }, [availableSlots, workflow?.scheduling?.timeSlot]);

  useEffect(() => {
    if (!item?.propertyID || !item?.building || !item?.requestType) return;

    const fetchItems = async () => {
      try {
        const res = await axios.get('v2/inventory/consume/supported-items', {
          params: {
            property_id: item.propertyID,
            building_name: item.building,
            request_type: item.requestType,
          },
        });
        if (res.data.success) setAvailableItems(res.data.items || []);
      } catch (err) {
        console.error(err);
      }
    };

    fetchItems();
  }, [item?.propertyID, item?.building, item?.requestType]);

  const itemOptions = useMemo(
    () =>
      availableItems.map((i) => ({
        value: i._id,
        label: i.name,
        quantity: i.quantity?.active || 0,
      })),
    [availableItems]
  );

  const getSignedUrl = useCallback(async (url) => {
    if (!url) return url;
    const batch = await getSignedUrlsBatched([url]);
    return batch[url] || url;
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: (acceptedFiles) => {
      if (pendingAttachments.length + acceptedFiles.length > 10) {
        toast.error('Maximum 10 files allowed');
        return;
      }
      const processed = acceptedFiles.map((file) => ({
        file,
        preview: URL.createObjectURL(file),
        name: file.name,
        type: file.type,
      }));
      setPendingAttachments((prev) => [...prev, ...processed]);
    },
    accept: 'image/*,video/*',
    maxSize: 50 * 1024 * 1024,
    multiple: true,
    maxFiles: 10,
  });

  const removePendingAttachment = (index) => {
    const file = pendingAttachments[index];
    if (file?.preview) URL.revokeObjectURL(file.preview);
    setPendingAttachments((prev) => prev.filter((_, i) => i !== index));
  };

  const uploadPendingAttachments = async () => {
    if (!pendingAttachments.length) return;
    const formData = new FormData();
    pendingAttachments.forEach((f) => formData.append('attachments', f.file));
    formData.append('property_id', item.propertyID);
    formData.append('ticket_id', item._id);

    setUploadingAttachments(true);
    try {
      const res = await axios.post('v2/maintenance/workflow/upload-attachments', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      if (res.data.success) {
        setAttachments((prev) => [...prev, ...res.data.attachments.map((a) => a.url)]);
        pendingAttachments.forEach((f) => f.preview && URL.revokeObjectURL(f.preview));
        setPendingAttachments([]);
      }
    } catch (err) {
      console.error('Error uploading attachments');
    } finally {
      setUploadingAttachments(false);
    }
  };

  const removeAttachment = (index) => {
    setAttachments((prev) => prev.filter((_, i) => i !== index));
  };

  const handleItemChange = (index, option) => {
    setSelectedItems((prev) => {
      const updated = [...prev];
      updated[index] = {
        ...updated[index],
        inventory_item_id: option?.value || null,
        name: option?.label || '',
        maxQuantity: option?.quantity || 0,
        consumed_quantity: 1,
        images_before: updated[index]?.images_before || [],
        images_after: updated[index]?.images_after || [],
      };
      return updated;
    });
  };

  const handleQuantityChange = (index, option) => {
    setSelectedItems((prev) => {
      const updated = [...prev];
      updated[index].consumed_quantity = option?.value || 1;
      return updated;
    });
  };

  const addItem = () => {
    setSelectedItems((prev) => [
      ...prev,
      {
        inventory_item_id: null,
        consumed_quantity: 1,
        notes: '',
        images_before: [],
        images_after: [],
      },
    ]);
  };

  const removeItem = (index) => {
    setSelectedItems((prev) => prev.filter((_, i) => i !== index));
  };

  const handleItemNotesChange = (index, value) => {
    setSelectedItems((prev) => {
      const updated = [...prev];
      updated[index].notes = value;
      return updated;
    });
  };

  const handleItemImageUpload = async (e, type, itemIndex) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;

    const formData = new FormData();
    files.forEach((f) => formData.append(type === 'before' ? 'images_before' : 'images_after', f));
    formData.append('property_id', item.propertyID);
    formData.append('item_id', selectedItems[itemIndex].inventory_item_id || 'temp');

    setLoading(true);
    try {
      const res = await axios.post('v2/inventory/consume/upload-images', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      if (res.data.success) {
        setSelectedItems((prev) => {
          const updated = [...prev];
          if (type === 'before') {
            updated[itemIndex].images_before = [
              ...updated[itemIndex].images_before,
              ...res.data.images.images_before.map((img) => img.url),
            ];
          } else {
            updated[itemIndex].images_after = [
              ...updated[itemIndex].images_after,
              ...res.data.images.images_after.map((img) => img.url),
            ];
          }
          return updated;
        });
      }
    } catch (err) {
      console.error('Error uploading images');
    } finally {
      setLoading(false);
    }
  };

  const removeItemImage = (itemIndex, imageIndex, type) => {
    setSelectedItems((prev) => {
      const updated = [...prev];
      if (type === 'before') {
        updated[itemIndex].images_before.splice(imageIndex, 1);
      } else {
        updated[itemIndex].images_after.splice(imageIndex, 1);
      }
      return updated;
    });
  };

  const openImagePreview = async (url, title = 'Image Preview', mediaType = null) => {
    setLoading(true);
    let previewUrl = url;
    const needsSigning =
      !(
        url.startsWith('blob:') ||
        url.startsWith('data:') ||
        url.includes('?') ||
        url.includes('Expires=') ||
        url.includes('Signature=') ||
        url.includes('X-Amz-')
      );

    if (needsSigning) {
      try {
        const signed = await getSignedUrlsBatched([url]);
        previewUrl = signed[url] || url;
      } catch { }
    }

    setImagePreviewModal({ url: previewUrl, title, mediaType });
    setLoading(false);
  };

  const closeImagePreview = () => setImagePreviewModal(null);

  const hasConsumedItems = useCallback(() => selectedItems.some((i) => i.inventory_item_id), [selectedItems]);

  const getValidConsumedItems = useCallback(() => selectedItems.filter((i) => i.inventory_item_id), [selectedItems]);

  const showConfirmationDialog = async () => {
    const hasAtt = attachments.length > 0 || pendingAttachments.length > 0;
    const hasInv = hasConsumedItems();

    if (!hasAtt && !hasInv) {
      const result = await Swal.fire({
        title: 'No Proof Added',
        html: `<p>Add photos/videos or inventory items for better records.</p><p>Continue anyway?</p>`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Yes, Submit',
        cancelButtonText: 'Add Proof',
      });
      return result.isConfirmed;
    }

    if (!hasAtt) {
      const result = await Swal.fire({
        title: 'No Visual Proof',
        html: `<p>Photos/videos are recommended.</p><p>Continue?</p>`,
        icon: 'info',
        showCancelButton: true,
        confirmButtonText: 'Continue',
        cancelButtonText: 'Add Media',
      });
      return result.isConfirmed;
    }

    return true;
  };

  const validateAndSubmit = async () => {
    const errors = [];

    if (ticketStatus === 'Scheduled') {
      if (!scheduleDate) errors.push('Schedule Date');
      if (!technicianName.trim()) errors.push('Technician Name');
      if (!selectedSlot) errors.push('Time Slot');
      if (!technicianPhone.trim()) errors.push('Technician Phone');
    }

    if (ticketStatus === 'Inprogress' && !progressNotes.trim()) {
      errors.push('Progress Notes required');
    }

    if (['Completed', 'Unresolved'].includes(ticketStatus) && !closureNotes.trim()) {
      errors.push('Closure Notes required');
    }

    if (ticketStatus === 'Completed' && timeTaken <= 0) {
      errors.push('Time Taken required');
    }

    if (ticketStatus === 'Completed' && hasConsumedItems()) {
      getValidConsumedItems().forEach((itm, idx) => {
        if (!itm.images_before?.length) errors.push(`Item #${idx + 1} → Missing Before Images`);
        if (!itm.images_after?.length) errors.push(`Item #${idx + 1} → Missing After Images`);
      });
    }

    if (errors.length) {
      setError(
        <div className="text-start">
          <ul className="mt-1 mb-0 ps-3">
            {errors.map((e, i) => (
              <li key={i} className="text-danger">
                {e}
              </li>
            ))}
          </ul>
        </div>
      );
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    setError('');

    if (ticketStatus === 'Completed') {
      const confirmed = await showConfirmationDialog();
      if (!confirmed) return;
    }

    await uploadPendingAttachments();

    setLoading(true);

    try {
      let consumedItemsResponse = null;

      if (ticketStatus === 'Completed' && hasConsumedItems()) {
        const payload = {
          ticket_id: item._id,
          property_id: item.propertyID,
          building_name: item.building,
          request_type: item.requestType,
          items: getValidConsumedItems().map((i) => ({
            inventory_item_id: i.inventory_item_id,
            consumed_quantity: i.consumed_quantity,
            notes: i.notes || '',
            images_before: i.images_before,
            images_after: i.images_after,
          })),
          notes,
        };

        const res = await axios.post('v2/inventory/consume', payload);
        if (res.data.success) {
          consumedItemsResponse = res.data.consumed;
        } else {
          setError(res.data.message || 'Consume failed');
          setLoading(false);
          return;
        }
      }

      const formData = new FormData();
      formData.append('ticketID', item._id);
      formData.append('status', ticketStatus);

      if (ticketStatus === 'Scheduled') {
        formData.append(
          'scheduling',
          JSON.stringify({
            date: scheduleDate,
            timeSlot: {
              start: formatMinutes(selectedSlot.start_time),
              end: formatMinutes(selectedSlot.end_time),
            },
            technicianName,
            technicianPhone,
            technicianId: currentUser?.id,
          })
        );
      }

      if (ticketStatus === 'Inprogress' && progressNotes) {
        formData.append(
          'progress',
          JSON.stringify({
            notes: progressNotes,
            partsOrdered: partsOrdered || '',
            expectedArrivalDate: expectedArrivalDate || null,
            followUpDate: followUpDate || null,
          })
        );
      }

      if (['Completed', 'Unresolved'].includes(ticketStatus)) {
        formData.append('closure', JSON.stringify({ notes: closureNotes }));
      }

      if (ticketStatus === 'Completed' && timeTaken > 0) {
        formData.append('timeTaken', timeTaken.toString());
      }

      if (consumedItemsResponse) {
        formData.append('consumedItems', JSON.stringify(consumedItemsResponse));
      }

      const res = await axios.post('v2/maintenance/workflow/update', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      if (res.data.success) {
        getTechnicalStaffTickets();
        getTotalTickets();
        onClose();
      }
    } catch (err) {
      console.error(err.response?.data?.message || 'Failed to update ticket');
    } finally {
      setLoading(false);
    }
  };

  const groupedTimeline = useMemo(() => {
    if (!workflow?.history?.length) return [];
    const grouped = [];
    let currentGroup = null;
    let recentStatus = null;
    const shouldGroupWithCurrent = (entry, group) => {
      if (!group) return false;
      const ca = group.entries[0].action;
      const na = entry.action;
      if (ca === 'status_changed' && recentStatus) {
        if (recentStatus === 'Scheduled' && na === 'scheduling_set') return true;
        if (recentStatus === 'Inprogress' && na === 'progress_updated') return true;
        if (recentStatus === 'Completed' && (na === 'closure_updated' || na === 'attachments_added')) return true;
      }
      if (ca === 'status_changed' && na === 'status_changed') {
        return entry.details.to === group.entries[0].details.to;
      }
      return false;
    };
    [...workflow.history].reverse().forEach(entry => {
      if (entry.action === 'status_changed') recentStatus = entry.details.to;
      if (shouldGroupWithCurrent(entry, currentGroup)) {
        currentGroup.entries.push(entry);
      } else {
        if (currentGroup) grouped.push(currentGroup);
        currentGroup = { entries: [entry] };
      }
    });
    if (currentGroup) grouped.push(currentGroup);
    return grouped;
  }, [workflow?.history]);

  if (!isOpen) return null;

  const selectedConfig = {
    Open: { color: 'info', icon: 'play-circle', label: 'Open Request' },
    Scheduled: { color: 'primary', icon: 'calendar', label: 'Visit Scheduled' },
    Inprogress: { color: 'warning', icon: 'tool', label: 'Work in Progress' },
    Completed: { color: 'success', icon: 'check-circle', label: 'Work Completed' },
    Unresolved: { color: 'danger', icon: 'alert-triangle', label: 'Issue Unresolved' },
  }[ticketStatus] || { color: 'secondary', icon: 'help-circle', label: ticketStatus };

  return (
    <>
      <Modal size="lg" isOpen={isOpen} toggle={onClose} centered backdrop="static" keyboard={false}>
        <div className="modal-header border-bottom d-flex align-items-center">
          <div className="d-flex align-items-center flex-grow-1">
            <FeatherIcon icon={selectedConfig.icon} size={22} className="text-primary me-2" />
            <h6 className="modal-title text-primary mb-0">Update Work Details — {selectedConfig.label}</h6>
          </div>
          <div className="d-flex align-items-center gap-2">
            <Button color="outline-info" size="sm" onClick={() => setModalOpen(true)}>
              <i className="fas fa-history me-1"></i> View History
            </Button>
            <button type="button" className="btn-close" onClick={onClose} aria-label="Close" />
          </div>
        </div>

        <ModalBody className="p-0">
          {error && (
            <Alert color="danger" className="border-0 rounded-3 shadow-sm mx-lg-4 my-3 px-4 py-3">
              <div className="d-flex align-items-start gap-3">
                <div
                  className="d-flex align-items-center justify-content-center rounded-circle"
                  style={{ width: 36, height: 36, backgroundColor: 'rgba(220,53,69,0.15)' }}
                >
                  <FeatherIcon icon="alert-circle" size={18} className="text-danger" />
                </div>
                <div>
                  <div className="fw-semibold text-danger mb-1">Please fix the following:</div>
                  <div className="text-danger small">{error}</div>
                </div>
              </div>
            </Alert>
          )}

          <Card className="border-0 m-3 mx-lg-4 mb-0">
            <CardBody className="p-4">
              {ticketStatus === 'Scheduled' && (
                <SchedulingSection
                  scheduleDate={scheduleDate}
                  setScheduleDate={setScheduleDate}
                  selectedSlot={selectedSlot}
                  setSelectedSlot={setSelectedSlot}
                  technicianName={technicianName}
                  setTechnicianName={setTechnicianName}
                  technicianPhone={technicianPhone}
                  setTechnicianPhone={setTechnicianPhone}
                  loadingAvailability={loadingAvailability}
                  availableSlots={availableSlots}
                  loading={loading}
                />
              )}

              {ticketStatus === 'Inprogress' && (
                <ProgressSection
                  progressNotes={progressNotes}
                  setProgressNotes={setProgressNotes}
                  partsOrdered={partsOrdered}
                  setPartsOrdered={setPartsOrdered}
                  expectedArrivalDate={expectedArrivalDate}
                  setExpectedArrivalDate={setExpectedArrivalDate}
                  followUpDate={followUpDate}
                  setFollowUpDate={setFollowUpDate}
                  loading={loading}
                />
              )}

              {['Completed', 'Unresolved'].includes(ticketStatus) &&
                ticketStatus !== 'Scheduled' &&
                ticketStatus !== 'Inprogress' && (
                  <Card className={`bg-${ticketStatus === 'Completed' ? 'success' : 'danger'} bg-opacity-10 border-0 shadow-sm`}>
                    <CardBody className="p-3 p-md-4">
                      <FormGroup className="mb-3">
                        <Label className="fw-semibold small mb-1">
                          {ticketStatus === 'Completed' ? 'Completion Notes' : 'Reason for Incompletion'}{' '}
                          <span className="text-danger">*</span>
                        </Label>
                        <Input
                          type="textarea"
                          rows={2}
                          value={closureNotes}
                          onChange={(e) => setClosureNotes(e.target.value)}
                          placeholder={
                            ticketStatus === 'Completed'
                              ? 'Brief summary of work done...'
                              : "Why couldn't this be completed..."
                          }
                          className="small"
                        />
                      </FormGroup>

                      {ticketStatus === 'Completed' && (
                        <>
                          <Row className="g-3 mb-3">
                            <Col md={5}>
                              <FormGroup className="mb-0">
                                <Label className="fw-semibold small mb-1">
                                  Time Taken <span className="text-danger">*</span>
                                </Label>
                                <Select
                                  value={generateTimeOptions.find((o) => o.value === timeTaken)}
                                  onChange={(o) => setTimeTaken(o?.value || 0)}
                                  options={generateTimeOptions}
                                  styles={customSelectStyles}
                                  placeholder="Select..."
                                  isClearable
                                  isDisabled={loading}
                                  className="small-select"
                                />
                              </FormGroup>
                            </Col>
                            <Col md={7}>
                              <FormGroup className="mb-0">
                                <Label className="fw-semibold small mb-1">Inventory Notes</Label>
                                <Input
                                  value={notes}
                                  onChange={(e) => setNotes(e.target.value)}
                                  placeholder="Notes about materials used..."
                                  className="small"
                                />
                              </FormGroup>
                            </Col>
                          </Row>

                          <div className="bg-white p-3 rounded border mb-3">
                            <div className="d-flex justify-content-between align-items-center mb-2">
                              <h6 className="fw-semibold small mb-0">
                                <FeatherIcon icon="package" size={14} className="me-1 text-warning" />
                                Items Used
                              </h6>
                              <Button
                                color="primary"
                                size="sm"
                                className="py-0 px-2 small"
                                onClick={addItem}
                                disabled={loading || !availableItems.length}
                              >
                                <FeatherIcon icon="plus" size={13} /> Add
                              </Button>
                            </div>

                            {selectedItems.length === 0 ? (
                              <div className="text-center py-3 text-muted small">No items added yet</div>
                            ) : (
                              <div className="space-y-3">
                                {selectedItems.map((inv, idx) => (
                                  <div key={idx} className="border rounded p-2 bg-light bg-opacity-50 mb-2">
                                    <div className="d-flex justify-content-between mb-2">
                                      <Badge color="secondary" className="px-2 py-1 small">
                                        Item {idx + 1}
                                      </Badge>
                                      <Button
                                        color="link"
                                        size="sm"
                                        className="p-0 text-danger"
                                        onClick={() => removeItem(idx)}
                                        disabled={loading}
                                      >
                                        <FeatherIcon icon="x" size={16} />
                                      </Button>
                                    </div>

                                    <Row className="g-2 mb-2">
                                      <Col xs={7}>
                                        <Select
                                          value={itemOptions.find((o) => o.value === inv.inventory_item_id)}
                                          onChange={(o) => handleItemChange(idx, o)}
                                          options={itemOptions}
                                          styles={customSelectStyles}
                                          placeholder="Select item..."
                                          isClearable
                                          isDisabled={loading}
                                          className="small-select"
                                        />
                                      </Col>

                                      {inv.inventory_item_id && (
                                        <Col xs={5}>
                                          <Select
                                            value={
                                              (() => {
                                                const max = Math.min(inv.maxQuantity || 10, 10);
                                                const opts = Array.from({ length: max }, (_, i) => ({
                                                  value: i + 1,
                                                  label: String(i + 1),
                                                }));
                                                return opts.find((o) => o.value === inv.consumed_quantity);
                                              })()
                                            }
                                            onChange={(o) => handleQuantityChange(idx, o)}
                                            options={() => {
                                              const max = Math.min(inv.maxQuantity || 10, 10);
                                              return Array.from({ length: max }, (_, i) => ({
                                                value: i + 1,
                                                label: String(i + 1),
                                              }));
                                            }}
                                            styles={customSelectStyles}
                                            isDisabled={loading}
                                            className="small-select"
                                          />
                                        </Col>
                                      )}
                                    </Row>

                                    <Input
                                      type="textarea"
                                      rows={1}
                                      value={inv.notes || ''}
                                      onChange={(e) => handleItemNotesChange(idx, e.target.value)}
                                      placeholder="Notes for this item..."
                                      className="small mb-2"
                                      disabled={loading}
                                    />

                                    {inv.inventory_item_id && (
                                      <div className="small">
                                        <Label className="fw-semibold small mb-1 d-flex align-items-center">
                                          <FeatherIcon icon="image" size={14} className="me-1 text-info" />
                                          Before & After photos <span className="text-danger ms-1">*</span>
                                        </Label>

                                        <Row className="g-2">
                                          <Col xs={6}>
                                            <div className="border border-dashed rounded p-2 text-center small bg-white">
                                              <Input
                                                type="file"
                                                multiple
                                                accept="image/*"
                                                id={`before-${idx}`}
                                                className="d-none"
                                                onChange={(e) => handleItemImageUpload(e, 'before', idx)}
                                              />
                                              <Label htmlFor={`before-${idx}`} className="mb-0 cursor-pointer text-muted">
                                                Before
                                              </Label>
                                            </div>

                                            {inv.images_before.length > 0 && (
                                              <div className="d-flex flex-wrap gap-1 mt-1">
                                                {inv.images_before.map((img, i2) => (
                                                  <div key={i2} className="position-relative" style={{ width: '48px' }}>
                                                    <MediaWithSignedUrl
                                                      mediaUrl={img}
                                                      className="rounded"
                                                      style={{ height: '48px', width: '48px', objectFit: 'cover' }}
                                                      onClick={() => openImagePreview(img)}
                                                      isThumbnail={true}
                                                    />
                                                    <Button
                                                      size="sm"
                                                      color="danger"
                                                      className="position-absolute top-0 start-100 translate-middle p-0 rounded-circle border border-white shadow-sm"
                                                      style={{ width: '18px', height: '18px', fontSize: '10px' }}
                                                      onClick={(e) => {
                                                        e.stopPropagation();
                                                        removeItemImage(idx, i2, 'before');
                                                      }}
                                                    >
                                                      ×
                                                    </Button>
                                                  </div>
                                                ))}
                                              </div>
                                            )}
                                          </Col>

                                          <Col xs={6}>
                                            <div className="border border-dashed rounded p-2 text-center small bg-white">
                                              <Input
                                                type="file"
                                                multiple
                                                accept="image/*"
                                                id={`after-${idx}`}
                                                className="d-none"
                                                onChange={(e) => handleItemImageUpload(e, 'after', idx)}
                                              />
                                              <Label htmlFor={`after-${idx}`} className="mb-0 cursor-pointer text-muted">
                                                After
                                              </Label>
                                            </div>

                                            {inv.images_after.length > 0 && (
                                              <div className="d-flex flex-wrap gap-1 mt-1">
                                                {inv.images_after.map((img, i2) => (
                                                  <div key={i2} className="position-relative" style={{ width: '48px' }}>
                                                    <MediaWithSignedUrl
                                                      mediaUrl={img}
                                                      className="rounded"
                                                      style={{ height: '48px', width: '48px', objectFit: 'cover' }}
                                                      onClick={() => openImagePreview(img)}
                                                      isThumbnail={true}
                                                    />
                                                    <Button
                                                      size="sm"
                                                      color="danger"
                                                      className="position-absolute top-0 start-100 translate-middle p-0 rounded-circle border border-white shadow-sm"
                                                      style={{ width: '18px', height: '18px', fontSize: '10px' }}
                                                      onClick={(e) => {
                                                        e.stopPropagation();
                                                        removeItemImage(idx, i2, 'after');
                                                      }}
                                                    >
                                                      ×
                                                    </Button>
                                                  </div>
                                                ))}
                                              </div>
                                            )}
                                          </Col>
                                        </Row>
                                      </div>
                                    )}
                                  </div>
                                ))}
                              </div>
                            )}

                            {hasConsumedItems() && (
                              <div className="small text-info mt-2">
                                <FeatherIcon icon="info" size={14} className="me-1" />
                                {getValidConsumedItems().length} item(s) will be deducted
                              </div>
                            )}
                          </div>

                          <div className="mt-3">
                            <Label className="fw-bold small mb-2 d-flex align-items-center">
                              <FeatherIcon icon="check-circle" size={15} className="me-2 text-success" />
                              Proof of Work <small className="text-muted ms-2">(optional)</small>
                            </Label>

                            <div
                              {...getRootProps()}
                              className={`border border-2 border-dashed rounded p-3 text-center transition-all cursor-pointer small ${isDragActive ? 'border-primary bg-primary bg-opacity-10' : 'border-secondary bg-light'
                                }`}
                            >
                              <input {...getInputProps()} />
                              <FeatherIcon
                                icon={isDragActive ? 'upload' : 'upload-cloud'}
                                size={28}
                                className={`text-${isDragActive ? 'primary' : 'muted'} mb-1`}
                              />
                              <div className="small fw-medium">{isDragActive ? 'Drop files here' : 'Click or drag files'}</div>
                              <div className="text-muted smallest">jpg, png, mp4 • max 50MB • up to 10 files</div>
                            </div>

                            {(pendingAttachments.length > 0 || attachments.length > 0) && (
                              <div className="mt-3">
                                {pendingAttachments.length > 0 && (
                                  <div className="mb-3">
                                    <div className="d-flex justify-content-between small mb-1">
                                      <span className="fw-medium">Pending ({pendingAttachments.length})</span>
                                      {pendingAttachments.length > 1 && (
                                        <Button
                                          color="link"
                                          size="sm"
                                          className="p-0 text-danger small"
                                          onClick={() => setPendingAttachments([])}
                                        >
                                          Clear
                                        </Button>
                                      )}
                                    </div>

                                    <div className="d-flex flex-wrap gap-2">
                                      {pendingAttachments.map((file, i) => (
                                        <div
                                          key={i}
                                          className="position-relative"
                                          style={{ width: '64px', cursor: 'pointer' }}
                                          onClick={() => openImagePreview(file.preview, file.name, file.type)}
                                        >
                                          {file.type.startsWith('video/') ? (
                                            <>
                                              <video
                                                src={file.preview}
                                                className="rounded border"
                                                style={{ height: '64px', width: '64px', objectFit: 'cover' }}
                                                preload="metadata"
                                              />
                                              <div
                                                className="position-absolute top-50 start-50 translate-middle bg-dark bg-opacity-50 rounded-circle d-flex align-items-center justify-content-center"
                                                style={{ width: '32px', height: '32px' }}
                                              >
                                                <FeatherIcon icon="play" size={14} className="text-white ms-1" />
                                              </div>
                                            </>
                                          ) : (
                                            <img
                                              src={file.preview}
                                              alt={file.name}
                                              className="rounded border"
                                              style={{ height: '64px', width: '64px', objectFit: 'cover' }}
                                            />
                                          )}

                                          <Button
                                            size="sm"
                                            color="danger"
                                            className="position-absolute top-0 end-0 p-0 rounded-circle shadow-sm border border-white"
                                            style={{ width: '18px', height: '18px', fontSize: '10px' }}
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              removePendingAttachment(i);
                                            }}
                                          >
                                            ×
                                          </Button>
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                )}

                                {attachments.length > 0 && (
                                  <div>
                                    <div className="small fw-medium mb-1">Uploaded ({attachments.length})</div>
                                    <div className="d-flex flex-wrap gap-2">
                                      {attachments.map((url, i) => (
                                        <div key={i} className="position-relative" style={{ width: '64px' }}>
                                          <MediaWithSignedUrl
                                            mediaUrl={url}
                                            className="rounded border"
                                            style={{ width: '64px', height: '64px', objectFit: 'cover', cursor: 'pointer' }}
                                            onClick={() => openImagePreview(url, `Proof ${i + 1}`)}
                                            isThumbnail={true}
                                          />
                                          <Button
                                            size="sm"
                                            color="danger"
                                            className="position-absolute top-0 end-0 p-0 rounded-circle shadow-sm border border-white"
                                            style={{ width: '18px', height: '18px', fontSize: '10px' }}
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              removeAttachment(i);
                                            }}
                                          >
                                            ×
                                          </Button>
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        </>
                      )}
                    </CardBody>
                  </Card>
                )}
            </CardBody>
          </Card>
        </ModalBody>

        <ModalFooter className="border-top bg-light px-4">
          <Button color="secondary" onClick={onClose} disabled={loading} className="px-4">
            Cancel
          </Button>
          <Button
            color={selectedConfig.color}
            size="lg"
            onClick={validateAndSubmit}
            disabled={loading}
            className="px-5"
          >
            {loading ? (
              <>
                <Spinner size="sm" className="me-2" />
                Processing...
              </>
            ) : (
              <>
                <FeatherIcon icon={selectedConfig.icon} className="me-2" />
                {ticketStatus === 'Scheduled' && 'Schedule Visit'}
                {ticketStatus === 'Inprogress' && 'Update Progress'}
                {ticketStatus === 'Completed' && 'Complete Ticket'}
                {ticketStatus === 'Unresolved' && 'Mark Unresolved'}
              </>
            )}
          </Button>
        </ModalFooter>
      </Modal>

      <Modal isOpen={modalOpen} toggle={() => setModalOpen(false)} size="xl" scrollable>
        <ModalHeader toggle={() => setModalOpen(false)}>
          <div className="d-flex align-items-center gap-3">
            <FeatherIcon icon="clock" size={20} className="text-primary" />
            <div>
              <h5 className="mb-0">Ticket Activity Timeline</h5>
              <small className="text-muted">
                Ticket ID: <strong>{item?.primaryID || '—'}</strong>
              </small>
            </div>
          </div>
        </ModalHeader>
        <ModalBody className="p-0">
          <div className="timeline-compact px-4 py-3">
            {workflow?.closure && workflow?.closure.closedAt && (
              <div className="closure-summary card border-0 bg-success-subtle mb-4 shadow-sm">
                <div className="card-body py-3 px-4">
                  <div className="d-flex align-items-start gap-3">
                    <div className="rounded-circle bg-success text-white p-2 d-flex">
                      <FeatherIcon icon="check-circle" size={20} />
                    </div>
                    <div className="flex-grow-1">
                      <div className="d-flex justify-content-between align-items-baseline">
                        <strong className="text-success">Ticket Completed</strong>
                        <small className="text-muted">
                          {new Date(workflow.closure.closedAt).toLocaleString([], {
                            dateStyle: 'medium',
                            timeStyle: 'short'
                          })}
                        </small>
                      </div>
                      <div className="small mt-1">
                        by {workflow.closure.closedBy?.first_name} {workflow.closure.closedBy?.last_name}
                      </div>
                      {workflow.closure.notes && (
                        <div className="mt-3">
                          <div className="small fw-semibold mb-1 text-success-emphasis">Completion Notes</div>
                          <div className="small bg-white p-2 rounded border">
                            {workflow.closure.notes}
                          </div>
                        </div>
                      )}
                      {workflow.closure.attachments?.length > 0 && (
                        <div className="mt-3">
                          <div className="small fw-semibold mb-2 d-flex align-items-center gap-2">
                            <FeatherIcon icon="image" size={14} />
                            Attachments ({workflow.closure.attachments.length})
                          </div>
                          <div className="d-flex flex-wrap gap-2">
                            {workflow.closure.attachments.map((att, idx) => (
                              <div
                                key={att._id || idx}
                                className="position-relative rounded overflow-hidden shadow-sm cursor-pointer"
                                style={{ width: '76px', height: '76px' }}
                                onClick={() => openImagePreview(att.url, `Attachment ${idx + 1}`, att.type)}
                              >
                                <MediaWithSignedUrl
                                  mediaUrl={att.url}
                                  className="w-100 h-100 object-fit-cover"
                                  isThumbnail={true}
                                />
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}
            <div className="timeline-items position-relative ps-4">
              <div className="timeline-line position-absolute start-0 top-0 bottom-0 w-px bg-secondary opacity-25" style={{ left: '11px' }} />
              {groupedTimeline.map((group, groupIdx) => {
                const first = group.entries[0];
                const allEntries = group.entries;
                const isStatusGroup = first.action === 'status_changed';
                const isSchedulingGroup = allEntries.some(e => e.action === 'scheduling_set');
                const isProgressGroup = allEntries.some(e => e.action === 'progress_updated');
                const isClosureGroup = allEntries.some(e => e.action === 'closure_updated') ||
                  allEntries.some(e => e.action === 'attachments_added');
                let color = 'secondary', icon = 'activity', title = first.action.replace(/_/g, ' ');
                let subtitle = '';
                let statusCount = 0;
                if (isStatusGroup) {
                  const status = first.details.to;
                  statusCount = allEntries.filter(e => e.action === 'status_changed').length;
                  color = status === 'Completed' ? 'success' :
                    status === 'Inprogress' ? 'warning' :
                      status === 'Scheduled' ? 'primary' : 'info';
                  icon = 'git-commit';
                  if (isSchedulingGroup) title = `Scheduled Visit`;
                  else if (isProgressGroup) title = `Work In Progress`;
                  else if (isClosureGroup) title = `Work Completed`;
                  else title = statusCount > 1 ? `Status → ${status} (${statusCount}×)` : `Status → ${status}`;
                } else if (first.action === 'scheduling_set') {
                  color = 'primary'; icon = 'calendar'; title = 'Visit Scheduled';
                } else if (first.action === 'progress_updated') {
                  color = 'warning'; icon = 'edit-3'; title = 'Progress Updated';
                } else if (first.action === 'closure_updated') {
                  color = 'success'; icon = 'check-square'; title = 'Closure Updated';
                } else if (first.action === 'attachments_added') {
                  color = 'info'; icon = 'image'; title = `Added ${first.details.count || '?'} attachment(s)`;
                }
                const latestEntry = allEntries[allEntries.length - 1];
                const timestamp = new Date(latestEntry.at).toLocaleString([], {
                  dateStyle: 'medium',
                  timeStyle: 'short'
                });
                return (
                  <div key={groupIdx} className="timeline-group mb-3">
                    <div className="d-flex position-relative">
                      <div
                        className={`timeline-dot rounded-circle d-flex align-items-center justify-content-center bg-${color} text-white flex-shrink-0`}
                        style={{ width: '26px', height: '26px', left: '-26px', top: '6px' }}
                      >
                        <FeatherIcon icon={icon} size={13} />
                      </div>
                      <div className="bg-white border rounded px-3 py-2 flex-grow-1 shadow-xs">
                        <div className="d-flex justify-content-between align-items-baseline small mb-1">
                          <strong className={`text-${color} mb-0`}>{title}</strong>
                          <span className="text-muted">
                            {timestamp}
                            {subtitle && <span className="ms-1 opacity-75">{subtitle}</span>}
                          </span>
                        </div>
                        <div className="text-muted smallest mb-2">
                          by {first.byName || 'System'}
                        </div>
                        <div className="timeline-group-content">
                          {isStatusGroup && first.details.from !== first.details.to && (
                            <div className="mb-2">
                              <Badge className="me-1 text-dark">{first.details.from}</Badge>
                              <FeatherIcon icon="arrow-right" size={12} className="text-muted mx-1" />
                              <Badge color={color} className="ms-1 text-dark">{first.details.to}</Badge>
                            </div>
                          )}
                          {isSchedulingGroup && (
                            <div className="bg-light p-2 rounded mb-2">
                              <div className="d-flex justify-content-between align-items-center mb-1">
                                <span className="fw-semibold text-primary">
                                  <FeatherIcon icon="calendar" size={12} className="me-1" />
                                  Schedule Details
                                </span>
                              </div>
                              <Row className="g-2">
                                <Col xs={4}>
                                  <div className="text-muted">
                                    <span>Date:</span>
                                    <span className="text-primary fw-medium ms-2">
                                      {new Date(allEntries.find(e => e.action === 'scheduling_set')?.details.date).toLocaleDateString()}
                                    </span>
                                  </div>
                                </Col>
                                <Col xs={4}>
                                  <div className="text-muted">
                                    <span>Time:</span>
                                    <span className="text-primary fw-medium ms-2">
                                      {allEntries.find(e => e.action === 'scheduling_set')?.details.timeSlot.start} – {allEntries.find(e => e.action === 'scheduling_set')?.details.timeSlot.end}
                                    </span>
                                  </div>
                                </Col>
                                {allEntries.find(e => e.action === 'scheduling_set')?.details.technicianName && (
                                  <Col xs={4}>
                                    <div className="text-muted">
                                      <span>Technician:</span>
                                      <span className="text-primary fw-medium ms-2">
                                        {allEntries.find(e => e.action === 'scheduling_set')?.details.technicianName}
                                      </span>
                                    </div>
                                  </Col>
                                )}
                              </Row>
                            </div>
                          )}
                          {isProgressGroup && (
                            <div className="bg-light p-2 rounded mb-2">
                              <div className="d-flex justify-content-between align-items-center mb-1">
                                <span className="fw-semibold text-warning">
                                  <FeatherIcon icon="tool" size={12} className="me-1" />
                                  Progress Update
                                </span>
                              </div>
                              <div className="progress-details">
                                {allEntries.find(e => e.action === 'progress_updated')?.details.new?.notes && (
                                  <div className="mb-2">
                                    <div className="fw-semibold mb-1">Notes:</div>
                                    <div className="text-muted bg-white p-1 rounded">
                                      {allEntries.find(e => e.action === 'progress_updated')?.details.new.notes.length > 100
                                        ? allEntries.find(e => e.action === 'progress_updated')?.details.new.notes.substring(0, 98) + '…'
                                        : allEntries.find(e => e.action === 'progress_updated')?.details.new.notes}
                                    </div>
                                  </div>
                                )}
                                <div className="row g-2 text-muted">
                                  {allEntries.find(e => e.action === 'progress_updated')?.details.new?.partsOrdered && (
                                    <Col xs={12} md={4}>
                                      <div>
                                        <span>Parts:</span>
                                        <span className="text-warning fw-medium ms-2">
                                          {allEntries.find(e => e.action === 'progress_updated')?.details.new.partsOrdered}
                                        </span>
                                      </div>
                                    </Col>
                                  )}
                                  {allEntries.find(e => e.action === 'progress_updated')?.details.new?.expectedArrivalDate && (
                                    <Col xs={6} md={4}>
                                      <div>
                                        <span>Arrival:</span>
                                        <span className="text-warning fw-medium ms-2">
                                          {new Date(allEntries.find(e => e.action === 'progress_updated')?.details.new.expectedArrivalDate).toLocaleDateString()}
                                        </span>
                                      </div>
                                    </Col>
                                  )}
                                  {allEntries.find(e => e.action === 'progress_updated')?.details.new?.followUpDate && (
                                    <Col xs={6} md={4}>
                                      <div>
                                        <span>Follow-up:</span>
                                        <span className="text-warning fw-medium ms-2">
                                          {new Date(allEntries.find(e => e.action === 'progress_updated')?.details.new.followUpDate).toLocaleDateString()}
                                        </span>
                                      </div>
                                    </Col>
                                  )}
                                </div>
                              </div>
                            </div>
                          )}
                          {isClosureGroup && (
                            <div className="bg-light p-2 rounded mb-2">
                              <div className="d-flex justify-content-between align-items-center mb-1">
                                <span className="fw-semibold text-success">
                                  <FeatherIcon icon="check-square" size={12} className="me-1" />
                                  Completion Details
                                </span>
                              </div>
                              {allEntries.find(e => e.action === 'closure_updated')?.details.new?.notes && (
                                <div className="mb-2">
                                  <div className="fw-semibold mb-1">Notes:</div>
                                  <div className="text-success bg-white p-1 rounded border-start border-3 border-success">
                                    {allEntries.find(e => e.action === 'closure_updated')?.details.new.notes.length > 120
                                      ? allEntries.find(e => e.action === 'closure_updated')?.details.new.notes.substring(0, 118) + '…'
                                      : allEntries.find(e => e.action === 'closure_updated')?.details.new.notes}
                                  </div>
                                </div>
                              )}
                              {allEntries.some(e => e.action === 'attachments_added') && (
                                <div className="mt-2">
                                  <div className="small fw-semibold mb-2 text-success">
                                    <FeatherIcon icon="image" size={12} className="me-1" />
                                    Attachments Added
                                  </div>
                                  <div className="d-flex flex-wrap gap-1">
                                    {allEntries
                                      .filter(e => e.action === 'attachments_added')
                                      .flatMap(e => e.details.files || [])
                                      .slice(0, 6)
                                      .map((f, i) => (
                                        <div
                                          key={i}
                                          className="rounded overflow-hidden shadow-xs cursor-pointer"
                                          style={{ width: '48px', height: '48px' }}
                                          onClick={() => openImagePreview(f.url, `Attachment ${i + 1}`, f.type)}
                                        >
                                          <MediaWithSignedUrl
                                            mediaUrl={f.url}
                                            className="w-100 h-100 object-fit-cover"
                                            isThumbnail={true}
                                          />
                                        </div>
                                      ))}
                                    {allEntries.filter(e => e.action === 'attachments_added').flatMap(e => e.details.files || []).length > 6 && (
                                      <div className="d-flex align-items-center justify-content-center bg-light border rounded text-muted" style={{ width: '48px', height: '48px' }}>
                                        <small>+{allEntries.filter(e => e.action === 'attachments_added').flatMap(e => e.details.files || []).length - 6}</small>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              )}
                            </div>
                          )}
                          {!isSchedulingGroup && !isProgressGroup && !isClosureGroup && (
                            <div className="small text-muted">{first.actionLabel || first.action.replace(/_/g, ' ')}</div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
              {(!workflow?.history || workflow.history.length === 0) && (
                <div className="text-center py-5 text-muted">
                  <FeatherIcon icon="clock" size={40} className="mb-3 opacity-50" />
                  <p className="mb-1">No activity recorded yet</p>
                </div>
              )}
            </div>
            {ticketHistory?.length > 0 && workflow?.history?.length > 8 && (
              <>
                <hr className="my-4 mx-4 opacity-50" />
                <div className="px-4 pb-3">
                  <h6 className="text-muted mb-2">
                    <FeatherIcon icon="list" size={14} className="me-1" />
                    Recent Activity (simplified)
                  </h6>
                  <div className="text-muted">
                    {ticketHistory.slice(-6).map((e, i) => (
                      <div key={i} className="d-flex justify-content-between py-1 border-bottom">
                        <span>{e.userName} — {e.action}</span>
                        <span>{new Date(e.createdAt).toLocaleString([], { timeStyle: 'short' })}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </>
            )}
          </div>
        </ModalBody>
        <ModalFooter className="bg-light border-0">
          <Button color="secondary" size="sm" onClick={() => setModalOpen(false)}>
            Close
          </Button>
        </ModalFooter>
      </Modal>

      <Modal
        isOpen={!!imagePreviewModal}
        toggle={closeImagePreview}
        size="xl"
        centered
        zIndex={2000}
        className="image-preview-modal"
      >
        <ModalHeader toggle={closeImagePreview}>{imagePreviewModal?.title || 'Media Preview'}</ModalHeader>
        <ModalBody className="text-center p-4">
          <div style={{ maxHeight: '75vh', overflow: 'auto' }}>
            <MediaWithSignedUrl
              mediaUrl={imagePreviewModal?.url}
              mediaType={imagePreviewModal?.mediaType}
              style={{
                maxHeight: '75vh',
                maxWidth: '100%',
                objectFit: 'contain',
                borderRadius: '8px',
                boxShadow: '0 10px 30px rgba(0,0,0,0.3)',
              }}
            />
          </div>
        </ModalBody>
        <ModalFooter>
          <Button
            color="primary"
            onClick={() => {
              const link = document.createElement('a');
              link.href = imagePreviewModal?.url;
              link.download = `${(imagePreviewModal?.title || 'preview').replace(/[^a-z0-9]/gi, '_')}.jpg`;
              link.click();
            }}
          >
            <FeatherIcon icon="download" className="me-2" />
            Download
          </Button>
          <Button color="secondary" onClick={closeImagePreview}>
            Close
          </Button>
        </ModalFooter>
      </Modal>
    </>
  );
};

export { MaintenanceFlowDetailsForm };